import { Button, Paper } from "@material-ui/core";
import axios from "axios";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BootstrapTable } from "react-bootstrap-table";

import _ from "lodash";
import { writeAoaToFile } from "../../utils/excelExport";
import "./Crud.css";
import Loader from "./loader/Loader";

const order = (key) => {
  if (/^[!-/:-@[-`{-~]/.test(key)) return `0${key}`; // Matches special characters, prefix with '0' to sort first
  if (/^\d/.test(key)) return `1${key}`; // Matches numbers, prefix with '1' to sort after special characters
  return `2${key}`; // Default for others, prefix with '2' to sort last
};

function Report({
  interval,
  controlledData,
  className = "",
  initialData = [],
  title,
  getUrl,
  getParams,
  getProcess,
  getPreProcess,
  getTotals,
  getGlobalTotals,
  getGroupOrders = [order],
  getGroupExtraOrder = [],
  groupBy,
  children,
  exportCSV = true,
  pagination = true,
  onRowClick,
  showTopTotals = false,
  renderTopTotals,
}) {
  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("userInfo")),
    []
  );
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [extraClasses, setExtraClasses] = useState("");
  const cancelTokenSource = useRef(null);

  useEffect(() => {
    if (controlledData) {
      setData(controlledData);
    }
  }, [controlledData]);

  /**
   * GET
   */
  const requestData = useCallback(() => {
    const currentCancelToken = cancelTokenSource.current;
    cancelTokenSource.current = axios.CancelToken.source();

    if (currentCancelToken) {
      currentCancelToken.cancel("Canceling previous request");
    }

    setLoading(true);
    const url = process.env.REACT_APP_API2_URL + getUrl(userInfo);
    axios
      .post(
        url,
        getParams instanceof Function ? getParams(userInfo) : undefined,
        {
          cancelToken: cancelTokenSource.current.token,
        }
      )
      .then((response) => response.data)
      .then((data) => {
        // Group By If Needed
        if (getPreProcess instanceof Function) {
          data = getPreProcess(data);
        }

        if (groupBy) {
          const groupedData = _.groupBy(data, groupBy);
          if (getProcess instanceof Function) {
            Object.keys(groupedData).forEach((key) => {
              groupedData[key] = getProcess(groupedData[key]);
            });
          }

          Object.keys(groupedData).forEach((key) => {
            groupedData[key] = groupedData[key].map((item, index) => ({
              ...item,
              Id: index,
            }));
          });

          setData(groupedData);
        } else {
          if (getProcess instanceof Function) {
            data = getProcess(data);
          }
          setData(
            data.map((item, index) => ({
              ...item,
              Id: index,
            }))
          );
        }

        setLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          console.error(err);
        }
      });

    return () => {
      currentCancelToken.cancel("Component unmounted");
    };
  }, [getUrl, userInfo, getProcess, getParams, groupBy]);

  /**
   * Initial load
   */
  useEffect(() => {
    let intervalId = 0;

    if (getUrl) {
      if (interval) {
        intervalId = setInterval(() => {
          requestData();
        }, interval); // Will trigger every second
      }
    }

    return () => clearInterval(intervalId);
  }, [getUrl, requestData, interval]);

  const onSearch = useCallback(() => {
    requestData();
  }, [requestData]);

  const exportXLSX = useCallback(async () => {
    const columns = React.Children.toArray(children)
      .filter((child) => !child.props.hidden)
      .map((child) => ({
        field: child.props.dataField,
        name: child.props.children,
        format: child.props.dataFormat,
      }));
  
    const params = Object.entries(getParams(userInfo)).filter(
      ([_, value]) => value
    );
  
    const worksheetData = [];
    worksheetData.push([title]);
    worksheetData.push([]);
    params.forEach((param) => worksheetData.push(param));
    worksheetData.push([]);
  
    if (!groupBy) {
      worksheetData.push(columns.map(({ name }) => name));
  
      data.forEach((item) => {
        const values = [];
        columns.forEach(({ field, format }) =>
          values.push(
            format instanceof Function
              ? String(format(item[field], null, true))
              : String(item[field])
          )
        );
        worksheetData.push(values);
      });
  
      if (getTotals) {
        worksheetData.push([]);
        const totals = getTotals(data);
        const values = [];
        columns.forEach(({ field, format }) =>
          values.push(
            format instanceof Function
              ? String(format(totals[field], null, true))
              : String(totals[field])
          )
        );
        worksheetData.push(values);
      }
    } else {
      sortedData.map(([group, groupItems]) => {
        worksheetData.push([group]);
        worksheetData.push(columns.map(({ name }) => name));
  
        groupItems.forEach((item) => {
          const values = [];
          columns.forEach(({ field, format }) =>
            values.push(
              format instanceof Function
                ? String(format(item[field], null, true))
                : String(item[field])
            )
          );
          worksheetData.push(values);
        });
  
        if (getTotals) {
          worksheetData.push([]);
          const totals = getTotals(groupItems);
          const values = [];
          columns.forEach(({ field, format }) =>
            values.push(
              format instanceof Function
                ? String(format(totals[field], null, true))
                : String(totals[field])
            )
          );
          worksheetData.push(values);
          worksheetData.push([totals.NumPlayers ? totals.NumPlayers : ""]);
          worksheetData.push([]);
        }
      });
  
      if (getGlobalTotals) {
        worksheetData.push([]);
        const totals = getGlobalTotals(Object.values(data).flat(Infinity));
        const values = [];
        columns.forEach(({ field, format }) =>
          values.push(
            format instanceof Function
              ? String(format(totals[field], null, true))
              : String(totals[field])
          )
        );
        worksheetData.push(values);
        worksheetData.push([totals.NumPlayers ? totals.NumPlayers : ""]);
      }
    }
  
    await writeAoaToFile(worksheetData, title, `${_.kebabCase(title)}.xlsx`);
  }, [data]);
  

  /**
   * TABLE OPTIONS
   */
  const options = {
    page: 1, // which page you want to show as default
    sizePerPage: 1000, // which size per page you want to locate as default
    sizePerPageList: [
      {
        text: "15",
        value: 15,
      },
      {
        text: "50",
        value: 50,
      },
      {
        text: "100",
        value: 100,
      },
      {
        text: "500",
        value: 500,
      },
      {
        text: "1000",
        value: 1000,
      },
    ], // you can change the dropdown list for size per page
    pageStartIndex: 1, // where to start counting the pages
    paginationSize: 5, // the pagination bar size.
    prePage: "Prev", // Previous page button text
    nextPage: "Next", // Next page button text
    firstPage: "First", // First page button text
    lastPage: "Last", // Last page button text
    paginationShowsTotal: true, // Accept bool or function
    paginationPosition: "top", // default is bottom, top and both is all available
    withFirstAndLast: true, // Hide the going to First and Last page button
    onRowClick,
  };

  const sortedData = useMemo(
    () =>
      _.sortBy(
        Object.entries(data),
        getGroupOrders.map(
          (sort) =>
            ([key, item]) =>
              sort(key, item)
        )
      ),
    [data]
  );

  const flatData = useMemo(() => {
  if (!groupBy) return Array.isArray(data) ? data : [];
  // data es objeto { groupKey: [items...] }
  return Object.values(data || {}).flat(Infinity);
  }, [data, groupBy]);

  const computedTopTotals = useMemo(() => {
    if (!showTopTotals) return null;

    if (groupBy) {
      if (getGlobalTotals instanceof Function) return getGlobalTotals(flatData);
      return null;
    }

    if (getTotals instanceof Function) return getTotals(data);
    return null;
  }, [showTopTotals, groupBy, getTotals, getGlobalTotals, data, flatData]);

  if (loading) return <Loader />;
  if ((groupBy && Array.isArray(data)) || (!groupBy && !Array.isArray(data)))
    return (
      <div
        id="main-div"
        className={`${className} ${extraClasses} !h-max flex flex-col gap-4`}
      >
        <div className="flex flex-row gap-4 mb-4">
          <Button
            type="button"
            onClick={onSearch}
            color="primary"
            variant="contained"
          >
            Search
          </Button>
        </div>
        <h2>{title}</h2>
      </div>
    );
  if (groupBy)
    return (
      <div
        id="main-div"
        className={`${className} ${extraClasses} !h-max flex flex-col gap-4`}
      >
        <div className="flex flex-row gap-4 mb-4">
          <Button
            type="button"
            onClick={onSearch}
            color="primary"
            variant="contained"
          >
            Search
          </Button>
          <Button
            type="button"
            onClick={exportXLSX}
            color="primary"
            variant="outlined"
          >
            Export XLSX
          </Button>
        </div>
        <h2>{title}</h2>
        <Paper className="flex flex-col gap-4">
          {showTopTotals && computedTopTotals && renderTopTotals ? (
            <div className="px-4 border bg-[#F8F9FA] rounded-md p-4 justify-center items-center flex uppercase tracking-wider">
              {renderTopTotals({
                totals: computedTopTotals,
                data: groupBy ? flatData : data,
                isGrouped: !!groupBy,
              })}
            </div>
          ) : null}
          {sortedData.map(([key, value], index) => (
            <React.Fragment key={key}>
              <h3>{key}</h3>
              <BootstrapTable
                // exportCSV={exportCSV}
                className="text-xs"
                data={value}
                options={options}
                pagination={false}
                version="4"
                hover
              >
                {children}
              </BootstrapTable>
              {getTotals ? (
                <>
                  <hr className="border-gray-200" />
                  <BootstrapTable
                    className="text-xs font-bold report-totals-row"
                    data={[
                      getTotals instanceof Function
                        ? getTotals(value)
                        : getTotals,
                    ]}
                    version="4"
                    hover
                    headerContainerClass="hidden"
                  >
                    {children}
                  </BootstrapTable>
                  {getTotals(value).NumPlayers && (
                    <h4 className="text-[12px] font-bold relative bottom-[12px] left-[5px]">{getTotals(value).NumPlayers} by Agent {key}</h4>
                  )}
                </>
              ) : null}
            </React.Fragment>
          ))}
          {getGlobalTotals ? (
            <>
              <hr className="border-gray-200" />
              <BootstrapTable
                className="text-xs font-bold report-totals-row"
                data={[
                  getGlobalTotals instanceof Function
                    ? getGlobalTotals(Object.values(data).flat(Infinity))
                    : getGlobalTotals,
                ]}
                version="4"
                hover
                headerContainerClass="hidden"
              >
                {children}
              </BootstrapTable>
              {getGlobalTotals(Object.values(data).flat(Infinity)).NumPlayers && (
                  <h4 className="text-[12px] font-bold relative bottom-[12px] left-[5px]">{getGlobalTotals(Object.values(data).flat(Infinity)).NumPlayers}</h4>
                )}
            </>
          ) : null}
        </Paper>
      </div>
    );

  return (
    <div id="main-div" className={`${className} !h-max`}>
      <div className="flex flex-row gap-4 mb-4">
        <Button
          type="button"
          onClick={onSearch}
          color="primary"
          variant="contained"
        >
          Search
        </Button>
        <Button
          type="button"
          onClick={exportXLSX}
          color="primary"
          variant="outlined"
        >
          Export XLSX
        </Button>
      </div>
      <Paper className="flex flex-col gap-4">
        <h2>{title}</h2>
        <BootstrapTable
          // exportCSV={exportCSV}
          className="text-xs"
          data={data}
          options={options}
          pagination={pagination}
          version="4"
          hover
        >
          {children}
        </BootstrapTable>
        {getTotals ? (
          <>
            <hr className="border-gray-200" />
            <BootstrapTable
              className="text-xs font-bold report-totals-row"
              data={[
                getTotals instanceof Function ? getTotals(data) : getTotals,
              ]}
              version="4"
              hover
              headerContainerClass="hidden"
            >
              {children}
            </BootstrapTable>
          </>
        ) : null}
      </Paper>
    </div>
  );
}

export default memo(Report);
